<!-- Doc: https://www.npmjs.com/package/vue-hash-calendar/v/1.4.30 -->

<template>
	<div
		class="c-calendar-wrap"
		:class="{
			'c-calendar-wrap--noToday': noToday
		}"
	>
		<div class="c-calendar-header">
			<div class="c-calendar-header-date">
				<div class="header-date-bold">{{ formatDateMonthAndDay }}</div>
				<div class="header-date-year">{{ formatDateYear }}</div>
			</div>
			<div class="c-calendar-header-control">
				<div class="header-control-today" @click="actionToday">{{ $t('enterprise.datepicker.today') }}</div>
			</div>
		</div>
		<vue-hash-calendar
			ref="vCalendar"
			visible
			isShowArrow
			isShowWeekView
			:defaultDatetime="innerDateValue"
			:isShowAction="false"
			:disabledWeekView="true"
			todayClassName="custom-calendar-today"
			checkedDayClassName="custom-calendar-checked"
			firstDayOfMonthClassName="custom-calendar-first"
			@change="changeCalendar"
			@click="clickCalendarCell"
		>
			<template v-slot:week="scope">
				<div class="slot-calendar-week">{{ translateWeek(scope.week) }}</div>
			</template>
			<template v-slot:day="scope">
				<div class="slot-calendar-mask">
					<template v-if="includeStyleControl('circle-calendar')">
						<div
							v-for="(color, index) in generateLimitDisplayedColors(scope.date)"
							:key="index"
							:style="insetCalendarColorVar(color)"
							class="slot-calendar-dot"
						></div>
					</template>
				</div>
				<div v-if="scope.extendAttr.isToday" class="slot-calendar-today">
					{{ scope.date.day }}
				</div>
				<div v-else class="slot-calendar-day">{{ scope.date.day }}</div>
			</template>
			<template v-slot:arrow="scope">
				<div class="arrow-wrap">
					<i :class="['iconfont', !scope.show && 'rotateIcon', 'icon-pull-down']"></i>
				</div>
			</template>
		</vue-hash-calendar>
	</div>
</template>

<script setup>
import moment from 'moment';
import { useScheduleCore } from '@/hooks/calendar/useScheduleCore';
import i18n, { EN } from '@/locales';
import { computed, ref, getCurrentInstance } from 'vue';

const { insetCalendarColorVar, calculateDateRange, filterDataByTimeRange } = useScheduleCore();
const { proxy } = getCurrentInstance();

const props = defineProps({
	value: null,
	scheduleData: {
		type: Array,
		default: () => []
	},
	noToday: {
		type: Boolean,
		default: false
	},
	styles: {
		// ['circle-calendar']
		type: Array,
		default: () => []
	}
});

const emits = defineEmits(['update:value', 'change', 'cell-click']);

const innerDateValue = computed({
	get: () => {
		if (!props.value) return new Date();
		return props.value;
	},
	set: val => {
		emits('update:value', val);
	}
});

const formatDateMonthAndDay = computed(() => {
	return moment(innerDateValue.value).format('MM/DD');
});

const formatDateYear = computed(() => {
	return moment(innerDateValue.value).format(`YYYY${proxy.$t('enterprise.datepicker.year')}`);
});



const includeStyleControl = styleType => {
	return props.styles.includes(styleType);
};

const vCalendar = ref(null);
const actionToday = () => {
	vCalendar.value.today();
};
const changeCalendar = date => {
	innerDateValue.value = date;
};
const clickCalendarCell = date => {
	emits('cell-click', date); 
};

// 日历圆点样式
const extractDistinctColors = date => {
	const originSchedule = props.scheduleData;
	const range = calculateDateRange(date, 'day');
	const rangeResult = filterDataByTimeRange(range, originSchedule);
	if (!rangeResult) return [];
	// 颜色去重
	return [...new Set(rangeResult.map(res => res.calendarThemeColor))];
};
const generateLimitDisplayedColors = date => {
	const momentDate = moment([date.year, date.month, date.day]);
	const toJsDate = momentDate._d;
	return extractDistinctColors(toJsDate).slice(0, 6);
};

const translateWeek = val => {
	const weekMap = {
		'一': 'M',
		'二': 'T',
		'三': 'W',
		'四': 'T',
		'五': 'F',
		'六': 'S',
		'日': 'S'
	};
	if (i18n.locale === EN) {
		return weekMap[val];
	}
	// 繁体中文字相同
	return val;
};
</script>

<style>
/* 定位同步 */
.calendar_week {
	left: 0.533333333333333vw !important;
}
/* .calendar_group_li {
	left: 0 !important;
} */
.calendar_item {
	position: relative;
}
</style>
<style lang="less">
.c-calendar-wrap {
	&--noToday {
		.custom-calendar-checked .slot-calendar-mask {
			background: transparent !important;
		}
	}

	.c-calendar-header {
		padding: 0.12rem 0.14rem;
		display: flex;
		justify-content: space-between;
		align-items: center;
		background: #fff;

		.c-calendar-header-date {
			display: flex;
			align-items: baseline;

			.header-date-bold {
				color: #333;
				text-align: right;
				font-family: Barlow;
				font-size: 0.22rem;
				 
				font-weight: 600;
				line-height: 140%; /* 30.8px */
			}
			.header-date-year {
				margin-left: 0.12rem;
				color: #333;
				text-align: right;
				 
				font-size: 0.12rem;
				 
				font-weight: 500;
				line-height: 0.18rem; /* 150% */
			}
		}
		.c-calendar-header-control {
			.header-control-today {
				min-width: 0.2rem;
				min-height: 0.2rem;

				padding: 0 0.04rem;
				text-align: center;
				color: #666666;
				line-height: 0.2rem;
				 
				font-weight: 500;
				font-size: 0.12rem;
				border-radius: 0.02rem;
				background: #f6f7f8;
			}
		}
	}

	.arrow-wrap {
		.icon-pull-down {
			display: inline-flex;
			font-size: 0.16rem;
			color: #989898;
		}
		.rotateIcon {
			transform: rotate(180deg);
		}
	}

	.slot-calendar-week {
		color: #989898;
		 
		font-size: 0.12rem;
		 
		font-weight: 500;
		line-height: normal;
	}
	.slot-calendar-day {
		text-align: center;
		font-family: Barlow;
		font-size: 0.16rem;
		 
		font-weight: 600;
		line-height: normal;
		position: relative;
	}
	.slot-calendar-today {
		color: var(--theme-color);
		 
		font-size: 0.15rem;
		 
		font-weight: 500;
		line-height: normal;
		position: relative;
	}
	.slot-calendar-mask {
		position: absolute;
		top: 2px;
		left: 0;
		right: 0;
		margin: auto;
		width: 0.28rem;
		height: 0.36rem;
		border-radius: 0.04rem;
		pointer-events: none;
		background: transparent;
		//transition: background 0.2s ease-in-out;

		.slot-calendar-dot {
			position: absolute;
			left: 0;
			right: 0;
			bottom: 0.04rem;
			margin: auto;
			width: 0.04rem;
			height: 0.04rem;
			border-radius: 50%;
			background: var(--calendar-color);
		}
	}
	.custom-calendar-checked {
		.slot-calendar-mask {
			// width: auto;
			background: linear-gradient(0deg, rgba(255, 255, 255, 0.9) 0, rgba(255, 255, 255, 0.9) 100%),
			var(--theme-color);
		}
		.slot-calendar-day {
			color: var(--theme-color);
		}
	}
}
</style>
